import type { TranslationLanguage } from "../types";

// Lazy-loaded so the ~900KB n-gram model only downloads when needed.
let eldPromise: Promise<(typeof import("eld/extrasmall"))["eld"]> | null = null;

const loadEld = () => {
  if (!eldPromise) eldPromise = import("eld/extrasmall").then((mod) => mod.eld);
  return eldPromise;
};

// Caches by trimmed text so re-rendering the same message body (e.g. on
// re-fetch or scroll) skips the synchronous eld.detect() call entirely.
const resultCache = new Map<string, TranslationLanguage | null>();

// content_plain keeps the full quoted thread for email replies (the UI needs
// it for "Show more"), but an English "On ... wrote:"/"From: ..." quote block
// mixed into an otherwise-foreign reply throws off the detector's confidence.
// Mirrors communication-service's stripQuotedReply — plain string search
// instead of a single regex to stay clear of catastrophic backtracking.
const isWhitespace = (char: string | undefined): boolean =>
  char !== undefined && /\s/.test(char);

const stripQuotedReply = (text: string): string => {
  const wroteIndex = text.indexOf("wrote:");
  if (wroteIndex !== -1) {
    const onIndex = text.lastIndexOf("On ", wroteIndex);
    const gap = onIndex === -1 ? -1 : wroteIndex - (onIndex + "On ".length);
    if (
      onIndex !== -1 &&
      gap >= 10 &&
      gap <= 80 &&
      (onIndex === 0 || isWhitespace(text[onIndex - 1]))
    )
      return text.slice(0, onIndex).trim();
  }

  const fromIndex = text.indexOf("From:");
  if (fromIndex > 0 && isWhitespace(text[fromIndex - 1]))
    return text.slice(0, fromIndex).trim();

  return text.trim();
};

export const detectMessageLanguage = async (
  text: string
): Promise<TranslationLanguage | null> => {
  const trimmed = stripQuotedReply(text);
  if (!trimmed) return null;

  const cached = resultCache.get(trimmed);
  if (cached !== undefined) return cached;

  const eld = await loadEld();
  const result = eld.detect(trimmed);
  const language =
    result.isReliable() &&
    (result.language === "en" || result.language === "es")
      ? result.language
      : null;

  resultCache.set(trimmed, language);
  return language;
};
